import type * as OrtNamespace from "onnxruntime-web";
import { disposeTensors, withOrtLock } from "./ort-lock";

const CHANNELS = 3;
const INPUT_SIZE = 224;

interface GpuBufferLike {
  readonly size: number;
  readonly mapState: "unmapped" | "pending" | "mapped";
  mapAsync(mode: number, offset?: number, size?: number): Promise<void>;
  getMappedRange(offset?: number, size?: number): ArrayBuffer;
  unmap(): void;
  destroy(): void;
}

interface GpuDeviceLike {
  readonly queue: {
    writeBuffer(buffer: GpuBufferLike, offset: number, data: ArrayBufferView): void;
    submit(commands: readonly unknown[]): void;
  };
  createBuffer(descriptor: { label: string; size: number; usage: number }): GpuBufferLike;
  createCommandEncoder(descriptor?: { label: string }): {
    copyBufferToBuffer(
      source: GpuBufferLike,
      sourceOffset: number,
      destination: GpuBufferLike,
      destinationOffset: number,
      size: number,
    ): void;
    finish(): unknown;
  };
}

interface GpuIo {
  device: GpuDeviceLike;
  inputBuffer: GpuBufferLike;
  outputBuffer: GpuBufferLike;
  readbackBuffer: GpuBufferLike;
  inputTensor: OrtNamespace.Tensor;
  outputTensor: OrtNamespace.Tensor;
  outputData: Float32Array;
  outputBytes: number;
}

const GPU_MAP_READ = 0x0001;
const GPU_COPY_SRC = 0x0004;
const GPU_COPY_DST = 0x0008;
const GPU_STORAGE = 0x0080;
const MAP_MODE_READ = 0x0001;
const align16 = (bytes: number) => Math.ceil(bytes / 16) * 16;

/**
 * The embedding session with fixed external WebGPU input/output storage.
 *
 * Recognition used to construct CPU tensors for every scan. WebGPU then registered,
 * uploaded and allocated their GPU counterparts for every run; even after Tensor.dispose,
 * WebKit could retain those native allocations until a later collection and kill the tab
 * after only a handful of scans. The detector already avoids that path. This gives the
 * embedder the same one-buffer-for-the-session lifetime and copies only its 192 floats
 * back to a reusable readback array.
 */
export class Embedder {
  readonly inputName: string;
  readonly outputName: string;

  private constructor(
    private readonly ort: typeof OrtNamespace,
    private readonly session: OrtNamespace.InferenceSession,
    private readonly dimension: number,
    private readonly gpuIo: GpuIo | null,
  ) {
    this.inputName = session.inputNames[0];
    this.outputName = session.outputNames[0];
  }

  static async create(
    ort: typeof OrtNamespace,
    model: Uint8Array,
    backend: string,
    dimension: number,
  ): Promise<Embedder> {
    const webgpu = backend === "webgpu";
    const session = await ort.InferenceSession.create(model, {
      executionProviders: [backend] as never,
      graphOptimizationLevel: "all",
    });
    if (!webgpu) {
      const embedder = new Embedder(ort, session, dimension, null);
      try {
        if (backend === "webgl") {
          // Compile every shader before the camera opens. Otherwise the first accepted
          // card pays this multi-second cost and makes the backend appear to freeze.
          await embedder.run(new Float32Array(CHANNELS * INPUT_SIZE * INPUT_SIZE), 1);
        }
        return embedder;
      } catch (error) {
        await session.release();
        throw error;
      }
    }

    let inputBuffer: GpuBufferLike | null = null;
    let outputBuffer: GpuBufferLike | null = null;
    let readbackBuffer: GpuBufferLike | null = null;
    try {
      const outputMeta = session.outputMetadata[0];
      const shape =
        outputMeta && !outputMeta.isTensor
          ? []
          : ((outputMeta?.shape ?? []) as ReadonlyArray<unknown>);
      const outputDimension =
        shape.length === 2 && typeof shape[1] === "number" ? (shape[1] as number) : 0;
      if (outputDimension !== dimension) {
        throw new Error(
          `embedder output changed from metadata (${JSON.stringify(shape)}, expected 1x${dimension})`,
        );
      }

      const device = (await ort.env.webgpu.device) as GpuDeviceLike;
      const inputBytes = CHANNELS * INPUT_SIZE * INPUT_SIZE * Float32Array.BYTES_PER_ELEMENT;
      const outputBytes = dimension * Float32Array.BYTES_PER_ELEMENT;
      inputBuffer = device.createBuffer({
        label: "card embedder input",
        size: align16(inputBytes),
        usage: GPU_STORAGE | GPU_COPY_DST,
      });
      outputBuffer = device.createBuffer({
        label: "card embedder output",
        size: align16(outputBytes),
        usage: GPU_STORAGE | GPU_COPY_SRC | GPU_COPY_DST,
      });
      readbackBuffer = device.createBuffer({
        label: "card embedder readback",
        size: align16(outputBytes),
        usage: GPU_MAP_READ | GPU_COPY_DST,
      });
      const gpuIo: GpuIo = {
        device,
        inputBuffer,
        outputBuffer,
        readbackBuffer,
        inputTensor: ort.Tensor.fromGpuBuffer(inputBuffer as OrtNamespace.Tensor.GpuBufferType, {
          dataType: "float32",
          dims: [1, CHANNELS, INPUT_SIZE, INPUT_SIZE],
        }),
        outputTensor: ort.Tensor.fromGpuBuffer(outputBuffer as OrtNamespace.Tensor.GpuBufferType, {
          dataType: "float32",
          dims: [1, dimension],
        }),
        outputData: new Float32Array(dimension),
        outputBytes,
      };
      return new Embedder(ort, session, dimension, gpuIo);
    } catch (error) {
      inputBuffer?.destroy();
      outputBuffer?.destroy();
      readbackBuffer?.destroy();
      await session.release();
      throw error;
    }
  }

  /** Run one or more CPU inputs; WebGPU deliberately has one fixed batch-1 binding. */
  async run(input: Float32Array, batch: number): Promise<Float32Array> {
    if (this.gpuIo) {
      if (batch !== 1) throw new Error("the persistent WebGPU embedder accepts batch 1");
      return withOrtLock(async () => {
        const io = this.gpuIo!;
        io.device.queue.writeBuffer(io.inputBuffer, 0, input);
        const result = await this.session.run(
          { [this.inputName]: io.inputTensor },
          { [this.outputName]: io.outputTensor },
        );
        if (result[this.outputName] !== io.outputTensor) {
          throw new Error("embedder replaced its preallocated WebGPU output");
        }
        const encoder = io.device.createCommandEncoder({ label: "card embedder readback" });
        encoder.copyBufferToBuffer(io.outputBuffer, 0, io.readbackBuffer, 0, io.outputBytes);
        io.device.queue.submit([encoder.finish()]);
        await io.readbackBuffer.mapAsync(MAP_MODE_READ, 0, io.outputBytes);
        try {
          io.outputData.set(
            new Float32Array(
              io.readbackBuffer.getMappedRange(0, io.outputBytes),
              0,
              this.dimension,
            ),
          );
        } finally {
          io.readbackBuffer.unmap();
        }
        return io.outputData;
      });
    }

    const tensor = new this.ort.Tensor("float32", input, [batch, CHANNELS, INPUT_SIZE, INPUT_SIZE]);
    let output: OrtNamespace.InferenceSession.ReturnType | null = null;
    try {
      output = await withOrtLock(() => this.session.run({ [this.inputName]: tensor }));
      return output[this.outputName].data as Float32Array;
    } finally {
      disposeTensors(tensor, ...Object.values(output ?? {}));
    }
  }
}
